/** Cross-tab JWT refresh coordination (localStorage lock + events). */

import { isAccessTokenExpiringSoon } from './tokenSession';

export const MEMBER_SESSION_EXPIRED_EVENT = 'member-session-expired';

const LOCKS = {
  member: 'member_token_refresh_lock',
  admin: 'admin_token_refresh_lock',
};

const LOCK_TTL_MS = 20_000;
const LOCK_POLL_MS = 200;
const LOCK_MAX_WAIT_MS = 12_000;

function lockKey(adminRequest) {
  return adminRequest ? LOCKS.admin : LOCKS.member;
}

function readLock(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.at) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearStaleLock(key) {
  const lock = readLock(key);
  if (!lock) return;
  if (Date.now() - lock.at > LOCK_TTL_MS) {
    localStorage.removeItem(key);
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Wait if another tab is refreshing; return true when access looks fresh again. */
export async function waitForCrossTabRefresh(adminRequest, getTokens) {
  const key = lockKey(adminRequest);
  const started = Date.now();

  while (Date.now() - started < LOCK_MAX_WAIT_MS) {
    clearStaleLock(key);
    const lock = readLock(key);
    const { access } = getTokens();

    if (!lock) {
      if (access && !isAccessTokenExpiringSoon(access)) {
        return access;
      }
      return null;
    }

    if (access && !isAccessTokenExpiringSoon(access)) {
      return access;
    }

    await sleep(LOCK_POLL_MS);
  }

  clearStaleLock(key);
  return null;
}

export function acquireCrossTabRefreshLock(adminRequest) {
  const key = lockKey(adminRequest);
  localStorage.setItem(key, JSON.stringify({ at: Date.now() }));
}

export function releaseCrossTabRefreshLock(adminRequest) {
  localStorage.removeItem(lockKey(adminRequest));
}

export function notifyMemberSessionExpired() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MEMBER_SESSION_EXPIRED_EVENT));
}
